import { SessionService } from './../services/session.service';
// Angular
import { Injectable } from '@angular/core';

// Third party
import { Observable, of } from 'rxjs';

// Local library
import {
  GUEST,
  IBaseResponseSlim,
  ICancelTicketResponse,
  IGetDutchWagersRequest,
  IGetExchangeResponse,
  IGetFileUploadTickets,
  IGetFileUploadTicketsCallResponse,
  IGetStackTicketsRequest,
  IGetTicketsSlimRequest,
  IStackTicketCancelOrDeleteResponse,
  IStackTicketIssueModel,
  ISubmitStackTicketRequest,
  ISubmitStackTicketsResponse,
  ITicketIssueModel,
  ITicketRequests,
  IToteCancelOrDeleteStackeRequest,
  IToteCancelWagerRequest,
  IToteDutchWageringGetCallsResponse,
  IToteStackedWageringGetCallResponse,
  IToteWageringCallResponse,
  IToteWageringGetCountersResponse,
  IToteWageringStackCallResponse
} from '@amwager/util-ts';

// Local file
import { HttpService } from '../services';



// TODO: write tests
@Injectable()
export class ToteWageringAPIService {
  private readonly API_URL = '/api/ToteWagering';

  constructor(
    private readonly httpService: HttpService,
    private readonly sessionService: SessionService,
  ) {}


  /**
   * GET API
   */

  /**
   * @description
   * Get manual transactions
   *
   * @param requests IGetTicketsSlimRequest
   * @returns Observable<{}>
   */
  // eslint-disable-next-line @typescript-eslint/ban-types
  getTicketsSlim$(requests: IGetTicketsSlimRequest): Observable<{}> {
    if (!requests?.runId) return of({});
    if (
      this.sessionService.getUserEmail() === GUEST
      || this.sessionService.getRoleId() === 9
    ) {
      return of({});
    }

    return this.httpService.get$({
      api: `${this.API_URL}/GetTicketsSlim`,
      params: this.httpService.objToHttpParams(requests as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get a list of dutch tickets form the system.
   * Notes: The date format MUST be yyyy-MM-dd HH:mm:ss e.g. 2016-07-24 23:50:23
   *
   * @param requests IGetDutchWagersRequest
   * @returns Observable<IToteDutchWageringGetCallsResponse>
   */
  getDutchTickets$(requests: IGetDutchWagersRequest)
    : Observable<IToteDutchWageringGetCallsResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetDutchTickets`,
      params: this.httpService.objToHttpParams(requests as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get tickets belonging to a file upload.
   * Notes: The date format MUST be MM-dd-yyyy e.g. 07-24-2016
   *
   * @param requests IGetFileUploadTickets
   * @returns Observable<IGetFileUploadTicketsCallResponse>
   */
  getFileUploadTickets$(requests: IGetFileUploadTickets)
    : Observable<IGetFileUploadTicketsCallResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetFileUploadTickets`,
      params: this.httpService.objToHttpParams(requests as unknown as Record<string, unknown>)
    });
  }

  /**
   * @description
   * Get a list of all pending exchange wagers for logged in user.
   *
   * @returns Observable<IGetExchangeResponse>
   */
  getExchange$(): Observable<IGetExchangeResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetExchange`
    });
  }

  /**
   * @description
   * Get a list of all stacked tickets matching query.
   * Notes: The date format MUST be yyyy-MM-dd HH:mm:ss e.g. 2016-07-24 12:50:23
   *
   * @param getStackTicketsRequest IGetStackTicketsRequest
   * @returns Observable<IToteStackedWageringGetCallResponse>
   */
  getStackedTickets$(getStackTicketsRequest: IGetStackTicketsRequest)
    : Observable<IToteStackedWageringGetCallResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetStackedTickets`,
      params: this.httpService.objToHttpParams({
        getStackTicketsRequest
      })
    });
  }

  /**
   * @description
   * Get a summary of all ticket activity for the day broken into tiers.
   * (Total, Event and Race)
   *
   * @param runid string
   * @returns Observable<IToteWageringGetCountersResponse>
   */
  getCounters$(runid: string): Observable<IToteWageringGetCountersResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/GetCounters`,
      params: this.httpService.objToHttpParams({
        runid
      })
    });
  }


  /**
   * POST API
   */

  /**
   * @description
   * Stack one or many tickets.
   *
   * @param compressedTickets ITicketRequests
   * @returns Observable<IBaseResponseSlim>
   */
  stackTickets$(compressedTickets: ITicketRequests)
    : Observable<IBaseResponseSlim> {
    return this.httpService.get$({
      api: `${this.API_URL}/StackTickets`,
      body: compressedTickets
    });
  }

  /**
   * @description
   * Attempts to submit stacked tickets to Tote. Any tickets matching query
   * will be submitted.
   *
   * @param submitStackTicketRequest ISubmitStackTicketRequest
   * @returns Observable<ISubmitStackTicketsResponse>
   */
  // submitStackTickets$(submitStackTicketRequest: ISubmitStackTicketRequest)
  //   : Observable<ISubmitStackTicketsResponse> {
  //   return this.httpService.get$({
  //     api: `${this.API_URL}/SubmitStackTickets`,
  //     body: submitStackTicketRequest
  //   });
  // }

  submitStackTickets$(submitStackTicketRequest: ISubmitStackTicketRequest)
    : Observable<ISubmitStackTicketsResponse> {
    return this.httpService.post$({
      api: `${this.API_URL}/SubmitStackTickets`,
      body: submitStackTicketRequest
    });
  }

  /**
   * @description
   * Issue one or many live wagers if valid. All valid wagers will be accepted,
   * any invalid wagers will be skipped.
   *
   * @param compressedTickets ITicketRequests
   * @returns Observable<IBaseResponseSlim>
   */
  issueTickets$(compressedTickets: ITicketRequests)
    : Observable<IBaseResponseSlim> {
    return this.httpService.get$({
      api: `${this.API_URL}/IssueTickets`,
      body: compressedTickets
    });
  }

  /**
   * @description
   * Issue a single wagering ticket if valid.
   *
   * @param ticketRequest ITicketIssueModel
   * @returns Observable<IToteWageringCallResponse>
   */
  issueSingleTicket$(ticketRequest: ITicketIssueModel)
    : Observable<IToteWageringCallResponse> {
    return this.httpService.post$({
      api: `${this.API_URL}/IssueSingleTicket`,
      body: ticketRequest
    });
  }

  /**
   * @description
   * Save a single ticket to the stack without issuing it.
   *
   * @param ticketRequest ITicketIssueModel
   * @returns Observable<IToteWageringStackCallResponse>
   */
  // stackSingleTicket$(ticketRequest: ITicketIssueModel)
  //   : Observable<IToteWageringStackCallResponse> {
  //   return this.httpService.get$({
  //     api: `${this.API_URL}/StackSingleTicket`,
  //     body: ticketRequest
  //   });
  // }

  stackSingleTicket$(ticketRequest: IStackTicketIssueModel)
    : Observable<IToteWageringStackCallResponse> {
    return this.httpService.post$({
      api: `${this.API_URL}/StackSingleTicket`,
      body: ticketRequest
    });
  }

  /**
   * @description
   * Cancel an already issued ticket if cancel requirements are met.
   *
   * @param ticketId string
   * @returns Observable<{}
   */
  // single ticket | ticketId
  // eslint-disable-next-line @typescript-eslint/ban-types
  cancelWager$(ticketId: string): Observable<{}> {
    return this.httpService.post$({
      api: `${this.API_URL}/CancelWager`,
      body: {'CompressedTickets': ticketId}
      // params: this.httpService.objToHttpParams({
      //   ticketId
      // })
    });
  }

  /**
   * @description
   * Cancel an already issued ticket if cancel requirements are met.
   *
   * @param ticketCancellRequest IToteCancelWagerRequest
   * @returns Observable<ICancelTicketResponse>
   */
  cancelTicket$(ticketCancellRequest: IToteCancelWagerRequest)
    : Observable<ICancelTicketResponse> {
    return this.httpService.get$({
      api: `${this.API_URL}/CancelTicket`,
      body: ticketCancellRequest
    });
  }

  /**
   * @description
   * Delete any stacked tickets matching query from queue.
   *
   * @param ticketsDeleteRequest IToteCancelOrDeleteStackeRequest
   * @returns Observable<IStackTicketCancelOrDeleteResponse>
   */
  // removeStackedTicket$(ticketsDeleteRequest: IToteCancelOrDeleteStackeRequest)
  //   : Observable<IStackTicketCancelOrDeleteResponse> {
  //   return this.httpService.get$({
  //     api: `${this.API_URL}/RemoveStackedTicket`,
  //     body: ticketsDeleteRequest
  //   });
  // }

  removeStackedTicket$(ticketsDeleteRequest: IToteCancelOrDeleteStackeRequest)
    : Observable<IStackTicketCancelOrDeleteResponse> {
    return this.httpService.post$({
      api: `${this.API_URL}/RemoveStackedTicket`,
      body: ticketsDeleteRequest
    });
  }

}
